// src/pages/AdminDataTools.jsx
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";

const API_BASE =
  process.env.NODE_ENV === "production"
    ? ""
    : process.env.REACT_APP_API_BASE || "";

function authHeaders(extra = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function fetchJson(url) {
  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders({ Accept: "application/json" }),
  });
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    // Normalize message
    const msg =
      typeof data === "string"
        ? data
        : data?.error || data?.message || JSON.stringify(data);
    const err = new Error(msg || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(body),
  });
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const msg =
      typeof data === "string"
        ? data
        : data?.error || data?.message || JSON.stringify(data);
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return data;
}

async function downloadFile(url, fallbackFilename) {
  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
  const filename = m && m[1] ? m[1] : fallbackFilename;

  const a = document.createElement("a");
  const objectUrl = window.URL.createObjectURL(blob);
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function normalizeCinemaRow(c) {
  const id = c?.cinema_id ?? c?.CINEMA_ID ?? c?.id ?? c?.ID ?? "";
  const name = c?.cinema_name ?? c?.CINEMA_NAME ?? c?.name ?? c?.NAME ?? "";
  return { id: String(id), name: String(name || "") };
}

async function loadCinemasWithFallback() {
  const endpoints = [
    `${API_BASE}/api/fiscal-devices/cinemas`,
    `${API_BASE}/api/cinemas_list`,
    `${API_BASE}/api/cinemas`,
  ];

  let lastErr = null;
  for (const url of endpoints) {
    try {
      const data = await fetchJson(url);
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.data)
        ? data.data
        : [];
      const norm = arr.map(normalizeCinemaRow).filter((x) => x.id && x.id !== "undefined");
      if (norm.length) return norm;
      // If endpoint works but returns empty, still accept it
      if (Array.isArray(arr)) return norm;
    } catch (e) {
      lastErr = e;
      continue;
    }
  }
  throw lastErr || new Error("Cannot load cinemas");
}

export default function AdminDataTools() {
  const navigate = useNavigate();
  const isAdmin = String(localStorage.getItem("is_admin") || "0") === "1";

  const [cinemas, setCinemas] = useState([]); // normalized: [{id,name}]
  const [cinemaId, setCinemaId] = useState("");
  const [from, setFrom] = useState(dayjs());
  const [to, setTo] = useState(dayjs());

  const [status, setStatus] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");

  // Compute formatted dates without hooks (avoids rules-of-hooks errors)
  const fromStr = from ? from.format("YYYY-MM-DD") : "";
  const toStr = to ? to.format("YYYY-MM-DD") : "";

  const cinemaName =
    cinemas.find((c) => String(c.id) === String(cinemaId))?.name || "";

  useEffect(() => {
    if (!isAdmin) return;

    (async () => {
      try {
        setStatus("");
        const norm = await loadCinemasWithFallback();
        setCinemas(norm);
        if (!cinemaId && norm[0]?.id) setCinemaId(String(norm[0].id));
      } catch (e) {
        console.error(e);
        setStatus(e.message || "Грешка при зареждане на кина");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const runPreview = async () => {
    if (!cinemaId) return;
    setStatus("Зареждане preview...");
    try {
      const data = await fetchJson(
        `${API_BASE}/api/tools/purge-preview?cinema_id=${encodeURIComponent(
          cinemaId
        )}&from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`
      );
      setPreview(data);
      setStatus("Preview готов.");
    } catch (e) {
      console.error(e);
      setStatus(e.message || "Грешка");
    }
  };

  const runPurge = async () => {
    if (!cinemaId) return;
    setStatus("Изтриване...");
    try {
      const data = await postJson(`${API_BASE}/api/tools/purge-run`, {
        cinema_id: Number(cinemaId),
        from: fromStr,
        to: toStr,
        confirm,
      });
      setPreview(data);
      setStatus("Готово. Изтрито.");
    } catch (e) {
      console.error(e);
      setStatus(e.message || "Грешка");
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Само администратор може да използва тези инструменти.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/admin/dashboard")}>
          Назад
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5">🗄️ Data Tools</Typography>
          <Button variant="outlined" onClick={() => navigate("/admin/dashboard")}>
            НАЗАД
          </Button>
        </Stack>

        {status ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {status}
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Избор на кино и период
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Кино</InputLabel>
                <Select
                  value={cinemaId}
                  label="Кино"
                  onChange={(e) => setCinemaId(e.target.value)}
                >
                  {cinemas.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name || "Cinema"} (#{c.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="От"
                value={from}
                onChange={(v) => setFrom(v || dayjs())}
                format="DD.MM.YYYY"
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="До"
                value={to}
                onChange={(v) => setTo(v || dayjs())}
                format="DD.MM.YYYY"
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Избрано: <b>{cinemaName || `#${cinemaId || "—"}`}</b> · {fromStr} →{" "}
            {toStr}
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Export
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              disabled={!cinemaId}
              onClick={async () => {
                try {
                  setStatus("");
                  await downloadFile(
                    `${API_BASE}/api/tools/export-xlsx?cinema_id=${encodeURIComponent(
                      cinemaId
                    )}&from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`,
                    `cinema_${cinemaId}_${fromStr}_to_${toStr}.xlsx`
                  );
                } catch (e) {
                  setStatus(e.message || "Грешка");
                }
              }}
            >
              EXCEL (.XLSX)
            </Button>

            <Button
              variant="outlined"
              disabled={!cinemaId}
              onClick={async () => {
                try {
                  setStatus("");
                  await downloadFile(
                    `${API_BASE}/api/tools/backup-sql?cinema_id=${encodeURIComponent(
                      cinemaId
                    )}&from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`,
                    `cinema_${cinemaId}_${fromStr}_to_${toStr}.sql`
                  );
                } catch (e) {
                  setStatus(e.message || "Грешка");
                }
              }}
            >
              CINEMA SQL (DATA)
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={async () => {
                try {
                  setStatus("");
                  await downloadFile(
                    `${API_BASE}/api/tools/backup-full-sql`,
                    `full_db_${dayjs().format("YYYY-MM-DD")}.sql`
                  );
                } catch (e) {
                  setStatus(e.message || "Грешка");
                }
              }}
            >
              FULL DB SQL (MYSQLDUMP)
            </Button>

            <Button variant="text" onClick={() => navigate("/admin/restore")}>
              RESTORE WIZARD →
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Purge (⚠️ изтриване по период)
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Първо натиснете <b>Preview</b>, после за изтриване въведете "DELETE".
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Button variant="outlined" onClick={runPreview} disabled={!cinemaId}>
              PREVIEW
            </Button>

            <TextField
              size="small"
              label='Type "DELETE"'
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              sx={{ width: { xs: "100%", sm: 220 } }}
            />

            <Button
              variant="contained"
              color="error"
              onClick={runPurge}
              disabled={!cinemaId || confirm !== "DELETE"}
            >
              PURGE
            </Button>
          </Stack>

          {preview ? (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Result
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: "#0b1220",
                  color: "#e5e7eb",
                  borderRadius: 1,
                  overflow: "auto",
                }}
              >
                {JSON.stringify(preview, null, 2)}
              </Box>
            </>
          ) : null}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}
